import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowUpRight, ArrowDown, Zap, ShieldCheck, BadgeIndianRupee } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?auto=format&fit=crop&w=1600&q=80";
const HERO_VIDEO = "https://videos.pexels.com/video-files/29543190/12717339_1920_1080_60fps.mp4";

const Counter = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => c.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
};

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    return () => v.removeEventListener("loadeddata", tryPlay);
  }, []);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40 pb-16 lg:pb-24">
      {/* ambient sun glow */}
      <motion.div
        style={{ y: yGlow }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-15%] h-[640px] w-[640px] rounded-full bg-gold/25 blur-[140px]"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(245,158,11,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.05) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
        }}
      />

      <motion.div style={{ opacity: fade }} className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="eyebrow flex items-center gap-3"
            >
              <span className="inline-block h-px w-10 bg-gold/60" />
              Maa Sakumbri Solar — maasakumbrisolar.com
            </motion.p>

            <h1 className="mt-7 font-display text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.6rem] font-extrabold tracking-tight">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                  Turn your rooftop
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
                  into a <span className="text-gold-grad">power plant.</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-7 max-w-xl text-[15px] sm:text-base leading-relaxed text-mutegray"
            >
              Premium solar panels, inverters, batteries and complete rooftop systems —
              designed, installed and maintained end-to-end by Maa Sakumbri Solar.
              Government subsidy support up to <span className="text-gold font-semibold">₹78,000</span> under
              PM Surya Ghar.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                type="button"
                data-testid="hero-cta-quote"
                onClick={() => scrollToId("quote")}
                className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_40px_rgba(245,158,11,0.45)]"
              >
                Get a free quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                type="button"
                data-testid="hero-cta-products"
                onClick={() => scrollToId("products")}
                className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 px-8 py-4 text-sm font-semibold text-warm transition-all duration-300 hover:border-gold/50 hover:text-gold"
              >
                Explore products
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </motion.div>
          </div>

          {/* hero visual */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] bg-gold/15 blur-2xl" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[1.6rem] border border-gold/20">
                <motion.div style={{ y: yImg }} className="relative h-[340px] sm:h-[440px] w-full">
                  <img
                    src={HERO_IMG}
                    alt="Solar panel array reflecting the golden hour sun"
                    className="ken-burns h-full w-full object-cover"
                    data-testid="hero-image"
                  />
                  <video
                    ref={videoRef}
                    src={HERO_VIDEO}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    onCanPlay={(e) => e.currentTarget.classList.remove("opacity-0")}
                    className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-1000"
                    data-testid="hero-video"
                  />
                </motion.div>
                <div
                  aria-hidden="true"
                  className="flare-sweep pointer-events-none absolute top-0 left-0 z-[1] h-full w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                />
                <div className="absolute inset-0 z-[2] bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 z-[3] flex items-end justify-between">
                  <div className="glass rounded-2xl border border-white/10 px-4 py-3">
                    <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-gold">
                      <Zap className="h-3.5 w-3.5" /> LIVE OUTPUT
                    </p>
                    <p className="mt-1 font-display text-lg font-bold text-warm">4.2 kW generating now</p>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="glass absolute -left-4 sm:-left-10 top-8 flex items-center gap-3 rounded-2xl border border-gold/25 px-4 py-3"
              >
                <ShieldCheck className="h-5 w-5 text-gold" />
                <div className="leading-tight">
                  <p className="text-xs font-bold text-warm">25-Year Warranty</p>
                  <p className="text-[10px] text-mutegray">Tier-1 N-Type modules</p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1.25 }}
                className="glass absolute -right-3 sm:-right-8 bottom-20 flex items-center gap-3 rounded-2xl border border-gold/25 px-4 py-3"
              >
                <BadgeIndianRupee className="h-5 w-5 text-gold" />
                <div className="leading-tight">
                  <p className="text-xs font-bold text-warm">₹78,000 Subsidy</p>
                  <p className="text-[10px] text-mutegray">PM Surya Ghar approved</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-16 lg:mt-24 grid grid-cols-2 gap-y-10 border-t border-white/10 pt-10 lg:grid-cols-4"
          data-testid="hero-stats"
        >
          {[
            { v: <Counter to={2500} suffix="+" />, l: "Homes powered" },
            { v: <Counter to={30} suffix=" MW+" />, l: "Capacity installed" },
            {
              v: <Counter to={4.9} decimals={1} suffix="/5" />,
              l: "Customer rating on Google",
              href: "https://www.google.com/maps/place/Maa+Sakumbri+Solar/@29.2905385,77.3505653,21z/data=!4m12!1m5!3m4!2zMjnCsDE3JzI2LjEiTiA3N8KwMjEnMDEuNSJF!8m2!3d29.2905833!4d77.3504167!3m5!1s0x390c31d8253e5bfd:0xad63a7fceb72852f!8m2!3d29.2905058!4d77.3504765!16s%2Fg%2F11zy3fj5fp",
              tid: "hero-google-rating-link",
            },
            { v: <Counter to={25} suffix="-yr" />, l: "Performance warranty" },
          ].map((s, i) => {
            const inner = (
              <>
                <p className="font-display text-3xl sm:text-4xl font-bold text-gold-grad">{s.v}</p>
                <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray transition-colors duration-300 group-hover:text-gold">
                  {s.l}
                  {s.href && (
                    <ArrowUpRight className="h-3 w-3 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </p>
              </>
            );
            const cls = "pr-6" + (s.href ? " group cursor-pointer" : "");
            return s.href ? (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={s.tid} className={cls}>
                {inner}
              </a>
            ) : (
              <div key={i} className={cls}>{inner}</div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
